import React, { useState, useEffect } from 'react';
import { Receipt, Download, Eye, Search, Filter, RefreshCw, CheckCircle, Clock, AlertCircle, XCircle, User, Calendar, Euro, ExternalLink, FileText, Sparkles, Shield } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

// Download custom branded PDF invoice
const downloadCustomPdf = async (invoiceId, invoiceNumber) => {
  try {
    toast.loading('Génération du PDF...', { id: 'pdf-download' });
    
    const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
      responseType: 'blob',
      withCredentials: true
    });
    
    // Create download link
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Facture_${invoiceNumber || invoiceId.slice(0, 8)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success('PDF téléchargé !', { id: 'pdf-download' });
  } catch (error) {
    console.error('Failed to download PDF:', error);
    toast.error('Erreur lors du téléchargement', { id: 'pdf-download' });
  }
};

// Download deposit deduction invoice PDF
const downloadDeductionPdf = async (invoiceId, invoiceNumber) => {
  try {
    toast.loading('Génération du PDF...', { id: 'pdf-download' });
    
    const response = await axios.get(`${API}/admin/deposit-deduction-invoices/${invoiceId}/pdf`, {
      responseType: 'blob',
      withCredentials: true
    });
    
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Facture_${invoiceNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success('PDF téléchargé !', { id: 'pdf-download' });
  } catch (error) {
    console.error('Failed to download PDF:', error);
    toast.error('Erreur lors du téléchargement', { id: 'pdf-download' });
  }
};

const InvoicesView = () => {
  const [invoices, setInvoices] = useState([]);
  const [deductionInvoices, setDeductionInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'stripe', 'deduction'
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAllInvoices();
  }, []);

  const fetchAllInvoices = async () => {
    setLoading(true);
    try {
      // Fetch both Stripe invoices and deduction invoices in parallel
      const [stripeRes, deductionRes] = await Promise.all([
        axios.get(`${API}/admin/invoices`, { withCredentials: true }),
        axios.get(`${API}/admin/deposit-deduction-invoices`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setInvoices(Array.isArray(stripeRes.data) ? stripeRes.data : []);
      setDeductionInvoices(Array.isArray(deductionRes.data) ? deductionRes.data : []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  const refreshInvoices = async () => {
    setRefreshing(true);
    await fetchAllInvoices();
    setRefreshing(false);
    toast.success('Factures mises à jour');
  };

  const getStatusBadge = (status, type) => {
    if (type === 'deduction') {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border bg-orange-500/20 text-orange-400 border-orange-500/30">
          <Shield className="w-3 h-3" />
          Retenue caution
        </span>
      );
    }
    const badges = {
      paid: { icon: CheckCircle, text: 'Payée', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      open: { icon: Clock, text: 'En attente', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      draft: { icon: FileText, text: 'Brouillon', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
      uncollectible: { icon: AlertCircle, text: 'Impayée', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
      void: { icon: XCircle, text: 'Annulée', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' }
    };
    const badge = badges[status] || badges.draft;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  // Combine and normalize invoices
  const allInvoices = [
    ...invoices.map(inv => ({ ...inv, type: 'stripe' })),
    ...deductionInvoices.map(inv => ({
      id: inv.id,
      number: inv.invoice_number,
      customer_name: inv.customer?.name || 'Client',
      customer_email: inv.customer?.email || '',
      created_date: inv.created_at ? new Date(inv.created_at).toLocaleDateString('fr-FR') : '-',
      amount_due: inv.total_ttc,
      amount_paid: inv.total_ttc,
      status: 'deduction',
      type: 'deduction',
      reservation_id: inv.reservation_id
    }))
  ].sort((a, b) => {
    // Sort by date descending
    const dateA = a.created_date ? new Date(a.created_date.split('/').reverse().join('-')) : new Date(0);
    const dateB = b.created_date ? new Date(b.created_date.split('/').reverse().join('-')) : new Date(0);
    return dateB - dateA;
  });

  const filteredInvoices = allInvoices.filter(invoice => {
    const matchesSearch = 
      invoice.number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customer_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    const matchesType = typeFilter === 'all' || invoice.type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  // Calculate stats
  const stats = {
    total: allInvoices.length,
    paid: invoices.filter(i => i.status === 'paid').length,
    pending: invoices.filter(i => i.status === 'open').length,
    deductions: deductionInvoices.length,
    totalRevenue: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.amount_paid || 0), 0),
    totalDeductions: deductionInvoices.reduce((sum, i) => sum + (i.total_ttc || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-[#DFFF00] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Facturation</h2>
          <p className="text-slate-400 text-sm mt-1">Factures Stripe & retenues sur caution</p>
        </div>
        <Button
          onClick={refreshInvoices}
          disabled={refreshing}
          variant="outline"
          className="border-slate-600 text-slate-300 hover:bg-slate-800"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
              <p className="text-slate-400 text-sm">Total</p>
            </div>
          </div>
        </div>
        
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.paid}</p>
              <p className="text-slate-400 text-sm">Payées</p>
            </div>
          </div>
        </div>
        
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.deductions}</p>
              <p className="text-slate-400 text-sm">Retenues</p>
            </div>
          </div>
        </div>
        
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#DFFF00]/20 flex items-center justify-center">
              <Euro className="w-5 h-5 text-[#DFFF00]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#DFFF00]">{formatAmount(stats.totalRevenue)}</p>
              <p className="text-slate-400 text-sm">CA encaissé</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <Euro className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-400">{formatAmount(stats.totalDeductions)}</p>
              <p className="text-slate-400 text-sm">Retenues</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher par n° facture, client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:border-[#DFFF00] focus:outline-none"
          >
            <option value="all">Tous les types</option>
            <option value="stripe">Factures Stripe</option>
            <option value="deduction">Retenues caution</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:border-[#DFFF00] focus:outline-none"
          >
            <option value="all">Tous les statuts</option>
            <option value="paid">Payées</option>
            <option value="open">En attente</option>
            <option value="deduction">Retenues</option>
          </select>
        </div>
      </div>

      {/* Invoices Table */}
      {filteredInvoices.length === 0 ? (
        <div className="bg-slate-800 rounded-xl p-12 text-center border border-slate-700">
          <Receipt className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Aucune facture trouvée</h3>
          <p className="text-slate-400">
            {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
              ? 'Aucune facture ne correspond à vos critères de recherche'
              : 'Les factures apparaîtront ici après les premiers paiements'}
          </p>
        </div>
      ) : (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Type</th>
                  <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">N° Facture</th>
                  <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Client</th>
                  <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Date</th>
                  <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Montant</th>
                  <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Statut</th>
                  <th className="text-right px-6 py-4 text-slate-400 font-medium text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                    <td className="px-6 py-4">
                      {invoice.type === 'deduction' ? (
                        <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-xs font-medium">
                          Retenue
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs font-medium">
                          Stripe
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white font-mono text-sm">{invoice.number || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-white font-medium">{invoice.customer_name || 'Client inconnu'}</p>
                        <p className="text-slate-400 text-sm">{invoice.customer_email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-300">{invoice.created_date}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`font-semibold ${invoice.type === 'deduction' ? 'text-orange-400' : 'text-white'}`}>
                        {formatAmount(invoice.amount_due)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(invoice.status, invoice.type)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {invoice.type === 'stripe' ? (
                          <>
                            {invoice.hosted_invoice_url && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                                className="text-slate-400 hover:text-white"
                                title="Voir la facture Stripe"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => downloadCustomPdf(invoice.id, invoice.number)}
                              className="text-[#DFFF00] hover:text-[#DFFF00] hover:bg-[#DFFF00]/10"
                              title="Télécharger PDF Phocea Rent"
                            >
                              <Sparkles className="w-4 h-4 mr-1" />
                              <Download className="w-4 h-4" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => downloadDeductionPdf(invoice.id, invoice.number)}
                            className="text-orange-400 hover:text-orange-400 hover:bg-orange-500/10"
                            title="Télécharger PDF retenue"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesView;
